import { HotspotGraphData, HotspotWithDistance } from "@nova/constants"

export const NUMBER_OF_BARS = 32
export const MIN_HEIGHT = 2
export const MAX_HEIGHT = 48
const TEMPERATURE_GRADIENT = ["#103F57", "#FA4F25", "#FF8F2C", "#FFEC00"]

/**
 * Divides a gradient of multiple hex codes into `n` discrete colors.
 */
const interpolateGradient = (hexColors: string[], n: number): string[] => {
    if (hexColors.length < 2) {
        throw new Error("At least two hex colors are required.")
    }

    const rgbToHex = (r: number, g: number, b: number) => {
        return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`
    }

    const rgbColors = hexColors.map((hex) => {
        const r = parseInt(hex.slice(1, 3), 16)
        const g = parseInt(hex.slice(3, 5), 16)
        const b = parseInt(hex.slice(5, 7), 16)
        return { r, g, b }
    })

    const colors: string[] = []

    const segments = hexColors.length - 1

    for (let i = 0; i < n; i++) {
        // Normalize t to range [0, 1].
        const t = i / (n - 1)

        // Determine which segment we are in.
        const scaledT = t * segments
        const index = Math.floor(scaledT)
        // Position within the segment.
        const localT = scaledT - index

        // Get RGB values for the two colors in the current segment.
        const startRGB = rgbColors[index]
        const endRGB = rgbColors[Math.min(index + 1, rgbColors.length - 1)]

        // Interpolate between the two colors.
        const r = Math.round(startRGB.r + localT * (endRGB.r - startRGB.r))
        const g = Math.round(startRGB.g + localT * (endRGB.g - startRGB.g))
        const b = Math.round(startRGB.b + localT * (endRGB.b - startRGB.b))

        colors.push(rgbToHex(r, g, b))
    }

    return colors
}

export const temperatureColors = interpolateGradient(TEMPERATURE_GRADIENT, NUMBER_OF_BARS)

export const thermalBinByHotspotId: Record<string, number> = {}

export const computeThermalGraph = ({
    hotspotsFilteredBySensitivityAndDistance,
}: {
    hotspotsFilteredBySensitivityAndDistance: HotspotWithDistance[]
}): HotspotGraphData => {
    // Based on hotspots that have already been filtered by sensitivity and
    // distance to perimeter
    const hotspotsWithTemperature = hotspotsFilteredBySensitivityAndDistance.filter(
        ({ temperature }) => temperature !== undefined
    ) as (HotspotWithDistance & { temperature: number })[]
    const temperatures = hotspotsWithTemperature.map(({ temperature }) => temperature)

    // A min/max pair that each bin in the graph will represent.
    const binRanges: { min: number; max: number }[] = []
    for (let i = 0; i < NUMBER_OF_BARS; i++) {
        const min = (i - 1) * 0.5
        binRanges.push({
            min,
            max: min + 0.5,
        })
    }

    //Same length array as hotspotSizes, but each value now represents
    const normalized = hotspotsWithTemperature.map((hotspot) => {
        const bin = binRanges.findIndex((bin) => hotspot.temperature < bin.max)
        const safeBin =
            bin === -1 ? NUMBER_OF_BARS - 1 : Math.min(Math.max(0, bin), NUMBER_OF_BARS - 1)

        thermalBinByHotspotId[hotspot.id] = safeBin

        return safeBin
    })

    const numberOfHotspotsPerBin: number[] = normalized.reduce((acc, cur) => {
        acc[cur]++
        return acc
    }, new Array(NUMBER_OF_BARS).fill(0))

    // This defines which value should be used to represent the bar in the graph that
    // will be the full height.
    const mostHotspotsPerBin = Math.max(...numberOfHotspotsPerBin)
    // This defines which vaue should be used to represent the bar in the graph that
    // will be the lowest height.
    const leastHotspotsPerBar = Math.min(...numberOfHotspotsPerBin)

    // Based on binRanges, but is an array of the LOWER bound of each bin.
    // This array has a length of NUMBER_OF_BARS + 1, where the last value technically
    // represents the upper bound of the last bar. The last value will be 0 if binRanges
    // is empty. The graph should NOT be displayed if barBounds.at(-1) === 0.
    const barBounds = [...binRanges.map(({ min }) => min), binRanges.at(-1)?.max || 0]

    // These define the absolute lower/upper bounds that the graph is capable
    // of representing.
    let absoluteGraphMinimum = Math.min(...temperatures)
    if (absoluteGraphMinimum === Infinity) absoluteGraphMinimum = 0
    const absoluteGraphMaximum = Math.max(...temperatures, 0)

    return {
        numberOfHotspotsPerBin,
        leastHotspotsPerBar,
        mostHotspotsPerBin,
        barBounds,
        absoluteGraphMinimum,
        absoluteGraphMaximum,
    }
}

const normalizeHotspotSizesForGraphWithPercentiles = (
    hotspotsWithSize: (HotspotWithDistance & { area: number })[],
    lowerPercentile: number = 0, // 0-1
    upperPercentile: number = 1, // 0-1
    onBinFound: (hotspotId: string, bin: number) => void
): { normalized: number[]; binRanges: { min: number; max: number }[] } => {
    if (hotspotsWithSize.length === 0) return { normalized: [], binRanges: [] }

    // Sort the numbers to compute percentiles.
    const sorted = [...hotspotsWithSize].sort((a, b) => a.area - b.area)
    const lowerIndex = Math.floor(lowerPercentile * (sorted.length - 1))
    const upperIndex = Math.floor(upperPercentile * (sorted.length - 1))
    const qLow = sorted[lowerIndex].area
    const qHigh = sorted[upperIndex].area

    // When qLow and qHigh are identical, return zeros.
    if (qLow === qHigh) {
        return {
            normalized: new Array(hotspotsWithSize.length).fill(0),
            binRanges: [{ min: qLow, max: qHigh }],
        }
    }

    // Normalize each value after clamping to [qLow, qHigh].
    const normalized = hotspotsWithSize.map((hotspot) => {
        const clamped = Math.min(Math.max(hotspot.area, qLow), qHigh)
        const scaled = ((clamped - qLow) / (qHigh - qLow)) * (NUMBER_OF_BARS - 1)
        const safeBin = Math.round(scaled)
        onBinFound(hotspot.id, safeBin)
        return safeBin
    })

    // Compute the bin ranges based on rounding thresholds.
    // The idea is that values mapping to bin i satisfy:
    //   scaled ∈ [i - 0.5, i + 0.5)
    // Solve for the original value: x = qLow + (scaled / (NUMBER_OF_BARS - 1)) * (qHigh - qLow)
    const binRanges: { min: number; max: number }[] = []
    for (let i = 0; i < NUMBER_OF_BARS; i++) {
        // Compute lower bound for bin i.
        let binMin: number
        if (i === 0) {
            binMin = qLow
        } else {
            binMin = qLow + ((i - 0.5) * (qHigh - qLow)) / (NUMBER_OF_BARS - 1)
        }

        // Compute upper bound for bin i.
        let binMax: number
        if (i === NUMBER_OF_BARS - 1) {
            binMax = qHigh
        } else {
            binMax = qLow + ((i + 0.5) * (qHigh - qLow)) / (NUMBER_OF_BARS - 1)
        }

        binRanges.push({ min: binMin, max: binMax })
    }

    return { normalized, binRanges }
}

export const sizeBinByHotspotId: Record<string, number> = {}

export const computeSizeGraph = ({
    hotspotsFilteredBySensitivityAndDistance,
    percentiles,
}: {
    hotspotsFilteredBySensitivityAndDistance: HotspotWithDistance[]
    percentiles: { upper: number; lower: number }
}): HotspotGraphData => {
    // List of hotspot areas, excluding hotspots that don't have an area defined.
    // Based on hotspots that have already been filtered by sensitivity and
    // distance to perimeter.
    const hotspotsWithArea = hotspotsFilteredBySensitivityAndDistance.filter(
        (hotspot) => hotspot.area !== undefined
    ) as (HotspotWithDistance & { area: number })[]
    const sizes = hotspotsWithArea.map(({ area }) => area)

    // normalized: Same length array as hotspotSizes, but each value now represents
    //             the bin# that the value belongs to.
    // binRanges: A min/max pair that each bin in the graph will represent.
    const { normalized, binRanges } = normalizeHotspotSizesForGraphWithPercentiles(
        hotspotsWithArea,
        percentiles.lower,
        percentiles.upper,
        (hotspotId, bin) => (sizeBinByHotspotId[hotspotId] = bin)
    )

    const numberOfHotspotsPerBin: number[] = normalized.reduce((acc, cur) => {
        acc[cur]++
        return acc
    }, new Array(NUMBER_OF_BARS).fill(0))

    // This defines which value should be used to represent the bar in the graph that
    // will be the full height.
    const mostHotspotsPerBin = Math.max(...numberOfHotspotsPerBin)
    // This defines which vaue should be used to represent the bar in the graph that
    // will be the lowest height.
    const leastHotspotsPerBar = Math.min(...numberOfHotspotsPerBin)

    // Based on binRanges, but is an array of the LOWER bound of each bin.
    // This array has a length of NUMBER_OF_BARS + 1, where the last value technically
    // represents the upper bound of the last bar. The last value will be 0 if binRanges
    // is empty. The graph should NOT be displayed if barBounds.at(-1) === 0.
    const barBounds = [...binRanges.map(({ min }) => min), binRanges.at(-1)?.max || 0]

    // These define the absolute lower/upper bounds that the graph is capable
    // of representing.
    let absoluteGraphMinimum = Math.min(...sizes)
    if (absoluteGraphMinimum === Infinity) absoluteGraphMinimum = 0
    const absoluteGraphMaximum = Math.max(...sizes, 0)

    return {
        numberOfHotspotsPerBin,
        leastHotspotsPerBar,
        mostHotspotsPerBin,
        barBounds,
        absoluteGraphMinimum,
        absoluteGraphMaximum,
    }
}

export const findBinForValue = ({ value, barBounds }: { value: number; barBounds: number[] }) => {
    // Scale the value by multiplying by 1000 and taking the floor.
    const target = Math.floor(value * 1000)

    // If the target matches the first element (after scaling), it falls in the first bin.
    if (target === Math.floor(barBounds[0] * 1000)) return 0

    // Iterate through the array starting at the second element.
    for (let i = 1; i < barBounds.length; i++) {
        const current = Math.floor(barBounds[i] * 1000)
        // If the target is less than or equal to the current boundary,
        // it belongs in the bin between arr[i-1] and arr[i]
        if (target <= current) {
            return i
        }
    }

    // If the target exceeds all boundaries, return the last bin index.
    return barBounds.length - 1
}
